import type { Creator, CreatorUgcOffer, WishlistCreator } from '@fluenceur/types';
import type { CollabWizardType } from '@/components/actions/send-invitation/collab-types';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import type {
  CollabReseedSeed,
  InvitationEntryIntent,
  InvitationState,
  InvitationView,
} from '@/stores/invitation-store';
import { createInvitationStore } from '@/stores/invitation-store';

const InvitationContext = createContext<StoreApi<InvitationState> | null>(null);

interface InvitationProviderProps {
  children: ReactNode;
  /**
   * Whether the modal is open. Drives the per-open re-seed: the store is a
   * shared singleton, so a reused modal instance must reset on each open or it
   * shows stale state from a previous flow.
   */
  isOpen: boolean;
  creator?: Creator; // Make creator optional for the new flow
  wishlistItems?: WishlistCreator[]; // For bulk wishlist invitations
  sourceRecommendationId?: string;
  /**
   * Brand-known email for the creator being invited (propose-collab entry). When
   * present it is seeded into the store so invitation creation persists it and
   * the flow can route to the EMAIL step instead of DM/comment.
   */
  recipientEmail?: string | null;
  /**
   * Seed the flow on the type step instead of jumping straight to program
   * selection. The "propose a collaboration" entries (contact card, wishlist
   * bulk action) pass `'typeSelection'`; every other entry omits it and keeps
   * its current view.
   */
  initialView?: Extract<InvitationView, 'typeSelection'>;
  /**
   * Resolved creator identity for the mono propose-collab entry (contact
   * card). Seeds the sponsored wizard's recipient; bulk entries resolve their
   * identities per line inside the wizard instead.
   */
  creatorIdentityId?: string | null;
  /**
   * Recipient display name/avatar for a propose-collab entry whose contact has
   * an identity but no social profile (no Creator object). Seeds the sponsored
   * wizard's recipient line. Ignored when a full `creator` is present.
   */
  recipientName?: string | null;
  recipientAvatarUrl?: string | null;
  /**
   * "Resend from an existing collab" entry (drawer): when present, the wizard
   * opens pre-filled on the `collabTerms` step with a blank sponsored brief
   * seeded from the previous collab. Additive — every other entry omits it.
   */
  seedCollab?: CollabReseedSeed | null;
  /** Brief to start the wizard on (with `initialView="typeSelection"`). */
  initialBriefTemplateId?: string;
  /**
   * The wizard path to open on the brief step (with `initialView="typeSelection"`):
   * the type of the brief it was launched from, or UGC from the contact sheet's
   * UGC row. Sponsored when a brief is named without it.
   */
  initialCollabType?: CollabWizardType;
  /**
   * The mono recipient's UGC offer, while her badge is active: it preselects UGC
   * on the type step and shows her starting price beside the amount.
   */
  recipientUgcOffer?: CreatorUgcOffer | null;
  /** The brief application the proposal answers (Candidatures entry). */
  briefApplicationId?: string;
  entryIntent?: InvitationEntryIntent;
}

export const InvitationProvider: React.FC<InvitationProviderProps> = ({
  children,
  isOpen,
  creator,
  wishlistItems,
  sourceRecommendationId,
  recipientEmail,
  initialView,
  creatorIdentityId,
  recipientName,
  recipientAvatarUrl,
  seedCollab,
  initialBriefTemplateId,
  initialCollabType,
  recipientUgcOffer,
  briefApplicationId,
  entryIntent = 'standard',
}) => {
  const storeRef = useRef<StoreApi<InvitationState> | null>(null);
  // Track whether the store has been seeded for the CURRENT open. Reset on close
  // so the next open re-seeds and a reused modal instance never shows stale
  // state from a previous flow.
  const seededForOpenRef = useRef(false);

  // One store PER provider instance. Several SendInvitationModal instances can
  // be mounted at once (the global contact-drawer one plus a page-level bulk
  // one); a module singleton let the last seed clobber the other, so a send
  // could target the wrong recipient.
  if (!storeRef.current) {
    storeRef.current = createInvitationStore();
  }

  // Reset + seed synchronously on each open (before children render), so children
  // always see fresh state matching the current props.
  if (!isOpen) {
    seededForOpenRef.current = false;
  } else if (!seededForOpenRef.current) {
    seededForOpenRef.current = true;
    // Seed a FRESH store instance instead of resetting the live one: writing to
    // the current store during render would synchronously notify subscribers
    // that are already mounted (a previous open's content, a co-mounted closed
    // modal), which React flags as "cannot update a component while rendering".
    // A brand-new store has no subscribers yet, so seeding it is silent; the
    // children receive it through the new context value and still see the fully
    // seeded state on their first render — the invariant is preserved by
    // construction. No reset needed: the fresh store IS the initial state.
    const store = createInvitationStore();
    storeRef.current = store;
    store.getState().setEntryIntent(entryIntent);

    // Set wishlist items if provided (for bulk invitations)
    // This skips inputSelection and goes directly to programSelection
    if (wishlistItems && wishlistItems.length > 0) {
      store.getState().setTarget({ type: 'wishlist-bulk', data: wishlistItems });
    }
    // Set creator if provided (for backward compatibility)
    else if (creator) {
      // Use setCreator which automatically sets the view to programSelection
      store.getState().setCreator(creator, sourceRecommendationId);
      // Carry the brand-known email so invitation creation persists it and the
      // details step can route to the EMAIL channel.
      store.getState().setRecipientEmail(recipientEmail ?? null);
      // Resolved identity for the sponsored wizard's mono recipient.
      store.getState().setCollabRecipientIdentityId(creatorIdentityId ?? null);
    }
    // Propose-collab for a contact with an identity but no social profile (never
    // linked to Instagram/TikTok): no Creator object exists, so seed the mono
    // recipient's identity, email and display fields directly. The sponsored
    // wizard's recipient line reads them instead of a Creator.
    else if (initialView === 'typeSelection') {
      store.getState().setRecipientEmail(recipientEmail ?? null);
      store.getState().setCollabRecipientIdentityId(creatorIdentityId ?? null);
      store
        .getState()
        .setCollabRecipientDisplay(
          recipientName
            ? { displayName: recipientName, avatarUrl: recipientAvatarUrl ?? null }
            : null
        );
      // Seed an email target so the Affiliation type can proceed: it hands off to
      // the program flow, which reads `target` and (for an email target) routes
      // to the email channel that resolves the recipient at send time. The
      // sponsored path ignores `target` (it reads the recipient identity/email
      // fields above), so this never affects it. When no email is known, no
      // target is seeded and the Affiliation type is gated at the type step.
      if (recipientEmail) {
        store.getState().setTarget({ type: 'email', data: { email: recipientEmail } });
      }
    } else if (recipientEmail) {
      store.getState().setTarget({ type: 'email', data: { email: recipientEmail } });
      store.getState().setRecipientEmail(recipientEmail);
    }
    // Propose-collab entries (contact card mono, wishlist bulk action): rewind
    // to the type step. Runs synchronously after the seed (which forced
    // programSelection) and before children render, so the flow opens on the
    // type step.
    if (initialView === 'typeSelection') {
      store.getState().setCurrentView('typeSelection');
      store.getState().setCollabRecipientUgcOffer(recipientUgcOffer ?? null);
      // Launched from a brief (brief editor, applications, proposed creators) or
      // from the contact sheet's UGC row: the type is decided and the brief step
      // opens, with that brief selected when one is named. Back still reaches
      // the type step.
      if (initialBriefTemplateId || initialCollabType) {
        store.getState().setCollabType(initialCollabType ?? 'sponsored');
        if (initialBriefTemplateId)
          store.getState().preselectCollabTemplate(initialBriefTemplateId);
        store.getState().setCurrentView('collabBrief');
      }
      store.getState().setCollabBriefApplicationId(briefApplicationId ?? null);
    }

    // "Resend from an existing collab" (drawer): pre-fill the sponsored terms
    // from the previous collab and jump straight to the terms step. The setter
    // sequence mirrors what the type + brief steps would have produced, so the
    // wizard picks up exactly where a manual "Sponsored → blank brief" would.
    if (seedCollab) {
      const state = store.getState();
      state.setCollabType('sponsored');
      // The reseed path never walks the draft step (where the "also save as
      // template" toggle lives): without this opt-out, the store's default-on
      // would silently create a library template on EVERY correct-and-resend.
      state.setSaveAsTemplate(false);
      state.setCollabRecipientIdentityId(seedCollab.recipientIdentityId);
      state.setCollabReseedRecipient({
        displayName: seedCollab.recipientDisplayName,
        avatarUrl: seedCollab.recipientAvatarUrl,
        email: seedCollab.recipientEmail,
      });
      state.setRecipientEmail(seedCollab.recipientEmail);
      state.setRespondByDays(seedCollab.respondByDays);
      // Only a re-proposal names a source brief. The server allows one per
      // unfunded brief and counts them by it, so the draft has to carry it all
      // the way to the send.
      state.setCollabReproposedFromCollabId(seedCollab.reproposedFromCollabId);
      state.setCollabBrief({
        mode: 'blank',
        templateId: null,
        name: seedCollab.name,
        content: seedCollab.content,
        compensationConfirmed: true,
      });
      state.setCurrentView('collabTerms');
    }
  }

  return (
    <InvitationContext.Provider value={storeRef.current}>{children}</InvitationContext.Provider>
  );
};

/**
 * Read a slice of the CURRENT provider's store (shallow-compared). This is the
 * only way views may consume invitation state — the store is per provider
 * instance, so importing a module-level hook would read the wrong store.
 */
export const useInvitationStoreSelector = <T,>(selector: (state: InvitationState) => T): T => {
  const storeApi = useContext(InvitationContext);
  if (!storeApi) {
    throw new Error('Missing InvitationProvider in the tree');
  }
  // Use the hook that explicitly supports the equality function
  return useStoreWithEqualityFn(storeApi, selector, shallow);
};

// Base hook kept as the internal alias for the specialized selectors below.
const useShallowInvitationStore = useInvitationStoreSelector;

// Essential specialized hooks using the base shallow hook
export const useInvitationData = () =>
  useShallowInvitationStore((state) => ({
    entryIntent: state.entryIntent,
    affiliateAddDraft: state.affiliateAddDraft,
    aiPersonalize: state.aiPersonalize,
    target: state.target,
    invitationType: state.invitationType,
    creator: state.creator,
    invitationData: state.invitationData,
    loading: state.loading,
    error: state.error,
    programs: state.programs,
    selectedProgram: state.selectedProgram,
    recipientEmail: state.recipientEmail,
    currentView: state.currentView,
    invitationChannelMode: state.invitationChannelMode,
    collabType: state.collabType,
    message: state.message,
    sourceRecommendationId: state.sourceRecommendationId,
    collabRecipientIdentityId: state.collabRecipientIdentityId,
    collabBriefMode: state.collabBriefMode,
    collabTemplateId: state.collabTemplateId,
    collabName: state.collabName,
    collabContent: state.collabContent,
    respondByDays: state.respondByDays,
    saveAsTemplate: state.saveAsTemplate,
    compensationConfirmed: state.compensationConfirmed,
    collabDeliveryMode: state.collabDeliveryMode,
    collabSendResults: state.collabSendResults,
    collabReproposedFromCollabId: state.collabReproposedFromCollabId,
    collabBriefApplicationId: state.collabBriefApplicationId,
    collabReseedRecipient: state.collabReseedRecipient,
    collabRecipientDisplay: state.collabRecipientDisplay,
    collabRecipientUgcOffer: state.collabRecipientUgcOffer,
  }));

export const useInvitationActions = () =>
  useShallowInvitationStore((state) => ({
    setAffiliateAddDraft: state.setAffiliateAddDraft,
    setTarget: state.setTarget,
    setCreator: state.setCreator,
    setPrograms: state.setPrograms,
    setSelectedProgram: state.setSelectedProgram,
    setInvitationData: state.setInvitationData,
    updateInvitationMethods: state.updateInvitationMethods,
    setLoading: state.setLoading,
    setError: state.setError,
    setCurrentView: state.setCurrentView,
    setInvitationChannelMode: state.setInvitationChannelMode,
    setCollabType: state.setCollabType,
    setCollabRecipientIdentityId: state.setCollabRecipientIdentityId,
    setCollabBrief: state.setCollabBrief,
    updateCollabContent: state.updateCollabContent,
    setRespondByDays: state.setRespondByDays,
    setSaveAsTemplate: state.setSaveAsTemplate,
    setCompensationConfirmed: state.setCompensationConfirmed,
    setCollabDeliveryMode: state.setCollabDeliveryMode,
    setCollabSendResults: state.setCollabSendResults,
  }));
